import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FileUploader} from 'ng2-file-upload';
import {DomSanitizer} from '@angular/platform-browser';
import {Subject} from 'rxjs';

@Component({
  selector: 'uploader-field',
  templateUrl: './uploader-field.component.html',
  styleUrls: ['./uploader-field.component.scss']
})

export class UploaderFieldComponent implements OnInit, OnDestroy {
  @Input() postUrl: string;
  @Input() types: string[];
  @Input() label: string;
  @Input() maxSize: number;
  @Input() maxWidth: number;
  @Input() popoverMessage: string;
  @Output() uploaderEmitter: EventEmitter<any> = new EventEmitter();

  _uploader: any;
  resizerSubsc$;
  fileUploadId;
  componentMessage = {value: undefined, css: ''};
  saving = false;
  resizerSubject: Subject<any> = new Subject();

  private iconFileOptions = {
    types: null,
    maxSize: 10 * 1024 * 1024 // 10MB and always > 0
  };

  constructor(private sanitizer: DomSanitizer) {
    this.fileUploadId = 'idUpload' + (new Date()).getTime() + Math.floor((Math.random() * 10000000) + 1);
  }

  ngOnInit() {
    if (!this.label) {
      this.label = 'Upload file';
    }
    if (this.maxSize) {
      this.iconFileOptions.maxSize = this.maxSize;
    }
    if (this.types) {
      this.iconFileOptions.types = this.types;
    }
    this.createUploader();
  }

  selectFile(event) {
    this.clearComponentMessage();

    if (!this._uploader.uploader.queue) {
      return;
    }
    if (this._uploader.uploader.queue.length === 0) {
      return;
    }

    const target = event.target || event.srcElement;
    this._uploader.filePath = target.value;
    const file = event.target.files[0];

    if (file) {
      if (this.maxSize && file.size > this.maxSize) {
        this.componentMessage.value = 'File size exceeds limit: ' + Math.round(this.iconFileOptions.maxSize / 1024 / 1024) + ' MB';
        this.componentMessage.css = 'error';
        return;
      }

      this._uploader.filePathDisplayer = this.sanitizer.bypassSecurityTrustUrl((window.URL.createObjectURL(file)));
      this._uploader.fileName = file.name;

      if (this.maxWidth !== undefined && this.isImage(file)) {

        if (this.resizerSubsc$) {
          this.resizerSubsc$.unsubscribe();
        }

        this.resizerSubsc$ = this.resizerSubject.subscribe(
          (resizedFile: any) => {
            this._uploader.uploader.clearQueue();
            this._uploader.uploader.addToQueue(
              [resizedFile],
              this.buildOptions(
                this.postUrl, this.iconFileOptions.types, this.iconFileOptions.maxSize
              ));

            this._uploader.uploader.queue[0].onProgress = (progress: any) => {
              if (progress === 100) {
                // TODO OR NOT
              }
            };

            this.uploaderEmitter.emit(this._uploader);

          },
          err => {
            console.error('Error while uploading file', err);
          }
        );
      } else {
          this._uploader.uploader.queue[0].onProgress = (progress: any) => {
              if (progress === 100) {
                  // TODO OR NOT
              }
          };
          this.uploaderEmitter.emit(this._uploader);
      }
    } else {
      delete this._uploader.filePathDisplayer;
      delete this._uploader.fileName;
      this.uploaderEmitter.emit(this._uploader);
    }
  }

  saveFile(): Promise<any> {
    return this._uploader.execute();
  }

  ngOnDestroy() {
    if (this.resizerSubsc$) {
      this.resizerSubsc$.unsubscribe();
    }
  }

  clearComponentMessage() {
    this.componentMessage = {
      value: '',
      css: ''
    };
  }

  private createUploader() {
    this._uploader = this.configUploader(this.postUrl, this.iconFileOptions.types, this.iconFileOptions.maxSize);

    const filterSize = this._uploader.uploader.options.filters.find(
      itm => (itm.name === 'fileSize')
    );

    if (filterSize) {
      filterSize.fn = itm => {
        if (itm.size > this.iconFileOptions.maxSize && this.maxWidth === undefined) {
          this.componentMessage.value = 'File size exceeds limit: ' + this.iconFileOptions.maxSize / 1024 / 1024 + 'MB';
          this.componentMessage.css = 'error';
          return false;
        }
        return true;
      };
    }

    this._uploader.uploader.onAfterAddingFile = f => {
      if (this._uploader.uploader.queue.length > 1) {
        this._uploader.uploader.queue.splice(0, 1);
      }
    };

    this.uploaderEmitter.emit(this._uploader);
  }


  private configUploader(
    url: string,
    fileTypes: string[] = null,
    maxFileSize: number = null): any { // type = icon or banner

    const uploader = new FileUploader(this.buildOptions(url, fileTypes, maxFileSize));

    return {
      uploader,
      execute:  (myUrl: string = null) => {

        if (myUrl) {
          uploader.setOptions({url: myUrl});
        }

        return new Promise<Response>((resolve, reject) => {
          uploader.onCompleteItem =
            (item, response, status, headers) => {
              try {
                const responseParsed = JSON.parse(response);

                if (responseParsed.status && responseParsed.status !== 200) {
                  reject(responseParsed);
                } else {
                  resolve(responseParsed);
                }
              } catch (err) {
                reject(err);
              }

            };

          uploader.onErrorItem = (item, response, status, headers) => {
            reject((response.indexOf('File too large') > 0) ? {fileError: 'File is too large. Try again'} : 'Error uploading file');
          };

          uploader.uploadAll();
        });
      }
    };
  }

  private buildOptions(
    url: string,
    fileTypes: string[] = null,
    maxFileSize: number = null) {

    const options: any = {};

    if (fileTypes) {
      options.allowedMimeType = fileTypes;
    }

    if (maxFileSize) {
      options.maxFileSize = maxFileSize;
    }

    if (url) {
      options.url = url;
    }

    options.disableMultipart = false;
    options.removeAfterUpload = true;

    return options;
  }

  private isImage(file: File) {
    return (file.type.split('/')[0] === 'image');

  }
}
